import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getNextTransactionForVerificationAsync,
  getTransactionsForVerificationAsync,
  selectTransactionsForVerificationList,
  selectTransactionsForVerificationStatus,
} from "./supportingDocumentsSlice";
import { useCallback, useEffect } from "react";
import {
  Page,
  Spacer,
  TableDS,
  TextCapitalized,
  ToastContainerDS,
  dateFormatterDayLongMonthYearHourMinuteSecond,
  triggerToast,
  unreachable,
} from "@qivia/ui";
import { TransactionsForVerificationListRowDisplay } from "./supportingDocumentsApi";
import { useNavigate, useParams } from "react-router-dom";
import { SupportingDocument } from "./supportingDocument";
import styled from "styled-components";
import "../../i18n/i18n";

export const SupportingDocuments = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const transactionsForVerificationList = useAppSelector(
    selectTransactionsForVerificationList,
  );

  const transactionsDisplayed: TransactionsForVerificationListRowDisplay[] =
    transactionsForVerificationList.map((t) => {
      return {
        uuid: t.uuid,
        category: t.category,
        company: t.company,
        executionDate: t.executionDate,
        startProcessingAt: t.startProcessingAt,
      };
    });
  const transactionsForVerificationStatus = useAppSelector(
    selectTransactionsForVerificationStatus,
  );

  useEffect(() => {
    if (transactionsForVerificationStatus === "idle") {
      void dispatch(getTransactionsForVerificationAsync());
    }
  }, [dispatch, transactionsForVerificationStatus]);

  const getSupportingDocumentData = useCallback(
    (id: string) => {
      navigate(`/home/supporting_documents/${id}`);
    },
    [navigate],
  );

  useEffect(() => {
    if (params.id) {
      navigate(`/home/supporting_documents/${params.id}`);
    }
  }, [navigate, params.id]);

  const headers = {
    uuid: {
      text: t("supportingDocuments.column.uuid"),
    },
    company: {
      text: t("supportingDocuments.column.company"),
    },
    category: {
      text: t("supportingDocuments.column.category"),
    },
    executionDate: {
      text: t("supportingDocuments.column.date"),
    },
    startProcessingAt: {
      text: t("supportingDocuments.column.startProcessingAt"),
    },
  };

  const render =
    (row: TransactionsForVerificationListRowDisplay) =>
    (key: keyof TransactionsForVerificationListRowDisplay) => {
      switch (key) {
        case "executionDate":
          return dateFormatterDayLongMonthYearHourMinuteSecond(
            new Date(row[key]),
          );
        case "category":
          return <TextCapitalized>{t(`category.${row[key]}`)}</TextCapitalized>;
        case "uuid":
        case "company":
          return row[key];
        case "startProcessingAt":
          if (row[key]) {
            return dateFormatterDayLongMonthYearHourMinuteSecond(
              new Date(row[key]),
            );
          }
          return "";
      }
      unreachable(key);
    };

  const handleNextTransaction = async () => {
    const nextTransaction = await dispatch(
      getNextTransactionForVerificationAsync(),
    ).unwrap();
    if (!nextTransaction) {
      triggerToast(
        t("supportingDocuments.noDocumentsToProcess") || "",
        "error",
      );
      return;
    }
    navigate(`/home/supporting_documents/${nextTransaction.uuid}`);
  };

  return (
    <>
      {params.id ? (
        <SupportingDocument />
      ) : (
        <Page
          title={t("supportingDocuments.title")}
          pageName={"supportingDocuments"}
          toaster={<ToastContainerDS />}
          cta={{
            title: t("supportingDocuments.button.startProcessing"),
            action: () => {
              void handleNextTransaction();
            },
            width: 40,
          }}
        >
          <TextCapitalized>{t("supportingDocuments.subTitle")}</TextCapitalized>
          <Spacer y={3} />
          <StyledTable>
            <TableDS<
              keyof TransactionsForVerificationListRowDisplay,
              TransactionsForVerificationListRowDisplay
            >
              data={transactionsDisplayed}
              headers={headers}
              render={render}
              onClickRow={(transaction) =>
                getSupportingDocumentData(transaction.uuid)
              }
            />
          </StyledTable>
        </Page>
      )}
    </>
  );
};

const StyledTable = styled.div`
  height: 100%;
  overflow: auto;
`;
